import { useState, useEffect } from "react";
import { useAppDispatch } from "../../app/hook";
import { useParams, useNavigate } from "react-router-dom";
import { showModal } from "../UI/modal/modal-slice";
import {
  SpaceBetween,
  Button,
  ColumnLayout,
  Form,
  Box,
} from "@cloudscape-design/components-themed";
import { AdjustmentForm } from "./AdjustmentForm";
import {
  ComprehensiveCompanySearchRequest,
  RateAdjustmentCaseContentCode,
  RateAdjustmentCaseContentData,
  RateAdjustmentCaseContentPutDeleteRequest,
  RateAdjustmentCaseContentRegisteredData,
  RateAdjustmentCaseContentRequest,
} from "../../features/api-interfaces";
import { COMMON_LABELS, DivisionDict } from "../default-constants";
import { LABELS, getIndustryConversionsItems } from "./constants";

import {
  useGetGwCompaniesQuery,
  useGetCommercialIndustriesQuery,
  useGetComprehensiveCompaniesQuery,
  usePostRateAdjustmentCaseContentMutation,
  usePutRateAdjustmentCaseContentMutation,
  useDeleteRateRateAdjustmentCaseContentMutation,
} from "../../features/api";
import {
  isNull,
  isLengthValid,
  isHalfNumberValid,
  isHalfNumberDecimalValid1 as isHalfNumberDecimalValid,
} from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import {
  formatDate,
  formatDateHyphen,
  formatDecimal,
  formatMultiplication,
} from "../../utils/format";
import { getComprehensiveMerchantCodeItems } from "../../utils/common";

/*===================================================================================
 * 料率調整案件内容登録更新画面
 * 概要：料率調整案件登録・更新画面を表示し、項目登録・更新機能を実装
 *
 * 前の画面からの必要なパラメーター
 * 料率調整案件No,加盟店枝番,調整案件枝番(更新画面の場合)
 *
 * 遷移先：なし
 * 戻り先：料率調整案件加盟店更新画面
 ===================================================================================*/
const Main = (props: any) => {
  const { rateAdjustmentCaseNo, merchantBranchNo, adjustmentBranchNo } =
    useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [postRateAdjustmentCaseContent] =
    usePostRateAdjustmentCaseContentMutation();
  const [putRateAdjustmentCaseContentMutation] =
    usePutRateAdjustmentCaseContentMutation();
  const [deleteRateAdjustmentCaseContentMutation] =
    useDeleteRateRateAdjustmentCaseContentMutation();

  // 登録画面か更新画面かのフラグ
  const isRegister = !adjustmentBranchNo;

  // GW一覧データの取得
  const { data: gw } = useGetGwCompaniesQuery(undefined);
  // GW一覧データの加工(viewから取得しているため決済GW事業者コードをキーに重複を削除)
  const uniqueData = gw
    ? Array.from(
        new Map(gw.map((item: any) => [item.gwCompanyCode, item])).values()
      )
    : [];

  const gwListData = uniqueData
    ? uniqueData.map((item: any) => ({
        label: item.gwCompanyName,
        value: item.gwCompanyCode,
      }))
    : [];

  //契約形態
  const contractRouteList = [
    { label: LABELS.DIRECT_CONTRACT, value: "1" },
    { label: LABELS.INCLUSION_CONTRACT, value: "2" },
  ];

  //費用負担
  const costList = [
    { label: LABELS.YES, value: true },
    { label: LABELS.NO, value: false },
  ];

  // 変換後業態コード一覧
  const [docomoDefinedIndustryList, setDocomoDefinedIndustryList] =
    useState<DivisionDict[]>();

  // 商用反映済みの業態情報を取得する
  const { data: industryAfterConversionCodeItems } =
    useGetCommercialIndustriesQuery();

  useEffect(() => {
    setDocomoDefinedIndustryList(
      getIndustryConversionsItems(industryAfterConversionCodeItems)
    );
  }, [industryAfterConversionCodeItems]);

  // 包括加盟店
  const [comprehensiveMerchant, setComprehensiveMerchant] = useState({
    value: "",
    label: "",
  });
  const [comprehensiveMerchantErrorMsg, setComprehensiveMerchantErrorMsg] =
    useState("");

  // 包括加盟店一覧
  const [comprehensiveMerchantList, setComprehensiveMerchantList] =
    useState<DivisionDict[]>();

  // 包括加盟店データを取得
  const comprehensiveCompanySearchRequest = {
    companyCode: "",
    invisibleComprehensiveCompanyNoList: [],
  } as ComprehensiveCompanySearchRequest;
  const { data: comprehensiveMerchantListItems } =
    useGetComprehensiveCompaniesQuery(comprehensiveCompanySearchRequest);

  useEffect(() => {
    setComprehensiveMerchantList(
      getComprehensiveMerchantCodeItems(comprehensiveMerchantListItems)
    );
  }, [comprehensiveMerchantListItems]);

  //登録済み料率調整案件情報取得API
  const registeredData: RateAdjustmentCaseContentRegisteredData =
    props.rateAdjustmentCaseData;

  //更新の場合のデータをセット
  useEffect(() => {
    if (
      registeredData !== undefined &&
      props.corpNum.adjustmentBranchNo !== undefined
    ) {
      setContractRoute(String(registeredData.contractRoute));
      //登録済みのデータ契約形態(直接)
      if (String(registeredData.contractRoute) !== "2") {
        setGwCompany({
          value: registeredData.settlementGwCompanyCode,
          label: registeredData.settlementGwCompanyName,
        });
        setDocomoDefinedIndustry({
          value: String(registeredData.docomoDefinitionIndustryNo),
          label: String(registeredData.industryName),
        });
        setComprehensiveMerchant({ value: "", label: "" });
        setAppliedIndustry("");
        //登録済みのデータ契約形態(包括)
      } else if (String(registeredData.contractRoute) !== "1") {
        setComprehensiveMerchant({
          value: registeredData.adjustmentsIncludeMerchantCompanyCode,
          label: registeredData.contractRouteName,
        });
        setAppliedIndustry(registeredData.applicationIndustry);
        setGwCompany({
          value: "",
          label: "",
        });
        setDocomoDefinedIndustry({
          value: "",
          label: "",
        });
      }
      setAnswerDate(formatDateHyphen(registeredData.responseDate));
      setApplicationDate(formatDateHyphen(registeredData.applicationDate));
      setAnswerRate(formatMultiplication(Number(registeredData.responseRate)));
      setDesiredRate(registeredData.desiredRate);
      setSales(String(registeredData.sales));
      setAllStoreNumber(String(registeredData.allShopsNumber));
      setStoreNumber(String(registeredData.installedShopsNumber));
      setHandlingProducts(registeredData.handlingProducts);
      setCost(registeredData.costBurdenFlag);
      //費用負担なし
      if (registeredData.costBurdenFlag !== true) {
        setCostAdjustInfo("");
        //費用負担あり
      } else {
        setCostAdjustInfo(
          String(registeredData.costBurdenAdjustmentInformation)
        );
      }
      setRateCriteria(registeredData.ratePresentationCriteria);
      setRemark(registeredData.note);
      setUpdateBy(registeredData.updatedBy);
      setUpdateAt(registeredData.updatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredData]);

  //戻るボタンの表示確認フラグ（入力フォームに何か入力された場合はtrue）
  const [isFormEntered, setIsFormEntered] = useState(false);

  //入力項目初期値とそのエラーメッセージ
  const [contractRoute, setContractRoute] = useState("1");
  const [gwCompany, setGwCompany] = useState({ value: "", label: "" });
  const [gwCompanyErrorMsg, setGwCompanyErrorMsg] = useState("");
  const [answerDate, setAnswerDate] = useState("");
  const [answerDateErrorMsg, setAnswerDateErrorMsg] = useState("");
  const [applicationDate, setApplicationDate] = useState("");
  const [applicationDateErrorMsg, setApplicationDateErrorMsg] = useState("");
  const [answerRate, setAnswerRate] = useState("");
  const [answerRateErrorMsg, setAnswerRateErrorMsg] = useState("");
  const [desiredRate, setDesiredRate] = useState("");
  const [desiredRateErrorMsg, setDesiredRateErrorMsg] = useState("");
  const [sales, setSales] = useState("");
  const [salesErrorMsg, setSalesErrorMsg] = useState("");
  const [allStoreNumber, setAllStoreNumber] = useState("");
  const [allStoreNumberErrorMsg, setAllStoreNumberErrorMsg] = useState("");
  const [storeNumber, setStoreNumber] = useState("");
  const [storeNumberErrorMsg, setStoreNumberErrorMsg] = useState("");
  const [docomoDefinedIndustry, setDocomoDefinedIndustry] = useState({
    value: "",
    label: "",
  });
  const [docomoDefinedIndustryErrorMsg, setDocomoDefinedIndustryErrorMsg] =
    useState("");
  const [appliedIndustry, setAppliedIndustry] = useState("");
  const [appliedIndustryErrorMsg, setAppliedIndustryErrorMsg] = useState("");
  const [handlingProducts, setHandlingProducts] = useState("");
  const [handlingProductsErrorMsg, setHandlingProductsErrorMsg] = useState("");
  const [cost, setCost] = useState(true);
  const [costErrorMsg, setCostErrorMsg] = useState("");
  const [costAdjustInfo, setCostAdjustInfo] = useState("");
  const [costAdjustInfoErrorMsg, setCostAdjustInfoErrorMsg] = useState("");
  const [rateCriteria, setRateCriteria] = useState("");
  const [rateCriteriaErrorMsg, setRateCriteriaErrorMsg] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkErrorMsg, setRemarkErrorMsg] = useState("");
  const [updateAt, setUpdateAt] = useState("");
  const [updateBy, setUpdateBy] = useState("");

  // 包括加盟店、GW事業者、ドコモ定義業態、申請日、回答日の入力チェック
  const checkRequired = (target: string, paramSetErrorMsg: any) => {
    // 必須
    if (isNull(target) || target === undefined) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    paramSetErrorMsg("");
    return true;
  };

  // 回答料率の入力チェック
  const checkAnswerRate = (target: string, paramSetErrorMsg: any) => {
    // 必須
    if (isNull(target)) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字種別
    if (!isHalfNumberDecimalValid(target)) {
      paramSetErrorMsg(getMessage("KGRERR079", 2, 2).label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // 希望料率の入力チェック
  const checkDesiredRate = (target: string, paramSetErrorMsg: any) => {
    // 必須
    if (isNull(target)) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字長
    if (!isLengthValid(target, 100)) {
      paramSetErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // 売上高の入力チェック
  const checkSales = (target: string, paramSetErrorMsg: any) => {
    // 必須
    if (isNull(target)) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字長
    if (!isLengthValid(target, 10)) {
      paramSetErrorMsg(getMessage("KGRERR059", 10).label);
      return false;
    }
    // 半角数値
    if (!isHalfNumberValid(target)) {
      paramSetErrorMsg(getMessage("KGRERR006").label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // 店舗数の入力チェック
  const checkStoreNumber = (
    target: string,
    paramSetErrorMsg: any,
    isNullCheck: boolean
  ) => {
    // 必須
    if (isNull(target) && isNullCheck) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字長
    if (!isLengthValid(target, 5)) {
      paramSetErrorMsg(getMessage("KGRERR059", 5).label);
      return false;
    }
    // 半角数値
    if (!isHalfNumberValid(target)) {
      paramSetErrorMsg(getMessage("KGRERR006").label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // 申請業態、取扱商材、料率提示条件の入力チェック
  const checkLengthHundred = (target: string, paramSetErrorMsg: any) => {
    // 文字長
    if (!isLengthValid(target, 100)) {
      paramSetErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // 費用負担調整情報の入力チェック
  const checkCostAdjustInfo = (target: string, paramSetErrorMsg: any) => {
    // 必須
    if (isNull(target)) {
      paramSetErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字長
    if (!isLengthValid(target, 50)) {
      paramSetErrorMsg(getMessage("KGRERR058", 50).label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };
  // 備考の入力チェック
  const checkRemark = (target: string, paramSetErrorMsg: any) => {
    // 文字長
    if (!isLengthValid(target, 400)) {
      paramSetErrorMsg(getMessage("KGRERR058", 400).label);
      return false;
    }

    paramSetErrorMsg("");
    return true;
  };

  // バリデーションチェック
  const checkInput = (): boolean => {
    let flag = true;
    let checks = [];
    if (contractRoute === "1") {
      checks = [
        checkRequired(gwCompany.value, setGwCompanyErrorMsg),
        checkRequired(applicationDate, setApplicationDateErrorMsg),
        checkRequired(answerDate, setAnswerDateErrorMsg),
        checkAnswerRate(answerRate, setAnswerRateErrorMsg),
        checkDesiredRate(desiredRate, setDesiredRateErrorMsg),
        checkSales(sales, setSalesErrorMsg),
        checkStoreNumber(allStoreNumber, setAllStoreNumberErrorMsg, false),
        checkStoreNumber(storeNumber, setStoreNumberErrorMsg, true),
        checkRequired(
          docomoDefinedIndustry.value,
          setDocomoDefinedIndustryErrorMsg
        ),
        checkLengthHundred(rateCriteria, setRateCriteriaErrorMsg),
        cost
          ? checkCostAdjustInfo(costAdjustInfo, setCostAdjustInfoErrorMsg)
          : true,
        checkRemark(remark, setRemarkErrorMsg),
      ];
    } else {
      checks = [
        checkRequired(
          comprehensiveMerchant.value,
          setComprehensiveMerchantErrorMsg
        ),
        checkRequired(applicationDate, setApplicationDateErrorMsg),
        checkRequired(answerDate, setAnswerDateErrorMsg),
        checkAnswerRate(answerRate, setAnswerRateErrorMsg),
        checkDesiredRate(desiredRate, setDesiredRateErrorMsg),
        checkSales(sales, setSalesErrorMsg),
        checkStoreNumber(allStoreNumber, setAllStoreNumberErrorMsg, false),
        checkStoreNumber(storeNumber, setStoreNumberErrorMsg, true),
        checkLengthHundred(appliedIndustry, setAppliedIndustryErrorMsg),
        checkLengthHundred(rateCriteria, setRateCriteriaErrorMsg),
        cost
          ? checkCostAdjustInfo(costAdjustInfo, setCostAdjustInfoErrorMsg)
          : true,
        checkRemark(remark, setRemarkErrorMsg),
      ];
    }

    checks.forEach(function (value) {
      if (value === false) {
        flag = false;
      }
    });
    return flag;
  };

  // 登録処理
  const registerRateAdjustmentCase = () => {
    //バリエーションチェック
    if (checkInput()) {
      //登録データ準備
      let adjustmentCaseData: RateAdjustmentCaseContentRequest = {
        data: {
          contractRoute: Number(contractRoute),
          adjustmentsIncludeMerchantCompanyCode: comprehensiveMerchant.value,
          settlementGwCompanyCode: gwCompany.value,
          applicationDate: formatDate(applicationDate),
          responseDate: formatDate(answerDate),
          responseRate: formatDecimal(Number(answerRate)),
          desiredRate: desiredRate,
          sales: Number(sales),
          allShopsNumber: Number(allStoreNumber),
          installedShopsNumber: Number(storeNumber),
          docomoDefinitionIndustryNo: Number(docomoDefinedIndustry.value),
          applicationIndustry: appliedIndustry,
          handlingProducts: handlingProducts,
          ratePresentationCriteria: rateCriteria,
          costBurdenFlag: cost,
          costBurdenAdjustmentInformation: costAdjustInfo,
          note: remark,
        },
        rateAdjustmentCaseNo: Number(rateAdjustmentCaseNo),
        merchantBranchNo: Number(merchantBranchNo),
      };

      //登録処理呼び出し
      postRateAdjustmentCaseContent(adjustmentCaseData).then((result: any) => {
        if (!("error" in result)) {
          navigate(
            `/rate-adjustment-case/merchant/${rateAdjustmentCaseNo}/${merchantBranchNo}`
          );
        }
      });
    }
  };

  // 更新処理
  const updateRateAdjustmentCase = () => {
    //バリエーションチェック
    if (checkInput()) {
      //更新データ
      let updateData: RateAdjustmentCaseContentData = {
        contractRoute: Number(contractRoute),
        adjustmentsIncludeMerchantCompanyCode: comprehensiveMerchant.value
          ? comprehensiveMerchant.value
          : "",
        settlementGwCompanyCode: gwCompany.value ? gwCompany.value : "",
        applicationDate: formatDate(applicationDate),
        responseDate: formatDate(answerDate),
        responseRate: formatDecimal(Number(answerRate)),
        desiredRate: desiredRate,
        sales: Number(sales),
        allShopsNumber: Number(allStoreNumber),
        installedShopsNumber: Number(storeNumber),
        docomoDefinitionIndustryNo: docomoDefinedIndustry.value
          ? Number(docomoDefinedIndustry.value)
          : NaN,
        applicationIndustry: appliedIndustry,
        handlingProducts: handlingProducts,
        ratePresentationCriteria: rateCriteria,
        costBurdenFlag: cost,
        costBurdenAdjustmentInformation: costAdjustInfo,
        note: remark,
      };
      let codeData: RateAdjustmentCaseContentCode = {
        rateAdjustmentCaseNo: Number(rateAdjustmentCaseNo),
        merchantBranchNo: Number(merchantBranchNo),
        adjustmentBranchNo: Number(adjustmentBranchNo),
      };
      let adjustmentCaseUpdateData: RateAdjustmentCaseContentPutDeleteRequest =
        {
          data: updateData,
          code: codeData,
        };
      //更新処理呼び出し
      putRateAdjustmentCaseContentMutation(adjustmentCaseUpdateData).then(
        (result: any) => {
          if (!("error" in result)) {
            navigate(
              `/rate-adjustment-case/merchant/${rateAdjustmentCaseNo}/${merchantBranchNo}`
            );
          }
        }
      );
    }
  };

  //削除処理
  const deleteRateAdjustmentCase = () => {
    let codeData: RateAdjustmentCaseContentCode = {
      rateAdjustmentCaseNo: Number(rateAdjustmentCaseNo),
      merchantBranchNo: Number(merchantBranchNo),
      adjustmentBranchNo: Number(adjustmentBranchNo),
    };
    let adjustmentCaseDeleteData: RateAdjustmentCaseContentPutDeleteRequest = {
      data: props.rateAdjustmentCaseData,
      code: codeData,
    };
    //更新処理呼び出し
    deleteRateAdjustmentCaseContentMutation(adjustmentCaseDeleteData).then(
      (result: any) => {
        if (!("error" in result)) {
          navigate(
            `/rate-adjustment-case/merchant/${rateAdjustmentCaseNo}/${merchantBranchNo}`
          );
        }
      }
    );
  };

  //登録ボタン
  const onClickRegister = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          registerRateAdjustmentCase();
        },
      })
    );
  };
  //更新ボタン
  const onClickUpdate = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          updateRateAdjustmentCase();
        },
      })
    );
  };
  //削除ボタン
  const onClickDelete = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          deleteRateAdjustmentCase();
        },
      })
    );
  };

  //戻るボタン
  const onClickReturn = () => {
    if (!isFormEntered) {
      navigate(
        `/rate-adjustment-case/merchant/${rateAdjustmentCaseNo}/${merchantBranchNo}`
      );
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate(
              `/rate-adjustment-case/merchant/${rateAdjustmentCaseNo}/${merchantBranchNo}`
            );
          },
        })
      );
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={<SpaceBetween direction="horizontal" size="xs"></SpaceBetween>}
      >
        <SpaceBetween size="l">
          <SpaceBetween direction="vertical" size="l">
            <AdjustmentForm
              isRegister={isRegister}
              setIsFormEntered={setIsFormEntered}
              //更新情報
              updateBy={updateBy}
              updateAt={updateAt}
              //契約ルート一覧
              contractRouteList={contractRouteList}
              //包括加盟店一覧
              inclusionMerchantListData={comprehensiveMerchantList}
              //GW一覧
              gwListData={gwListData}
              //業態一覧
              docomoDefinedIndustryList={docomoDefinedIndustryList}
              //費用負担一覧
              costList={costList}
              //入力項目
              contractRoute={contractRoute}
              setContractRoute={setContractRoute}
              inclusionMerchant={comprehensiveMerchant}
              setInclusionMerchant={setComprehensiveMerchant}
              gwCompany={gwCompany}
              setGwCompany={setGwCompany}
              answerDate={answerDate}
              setAnswerDate={setAnswerDate}
              applicationDate={applicationDate}
              setApplicationDate={setApplicationDate}
              answerRate={answerRate}
              setAnswerRate={setAnswerRate}
              desiredRate={desiredRate}
              setDesiredRate={setDesiredRate}
              sales={sales}
              setSales={setSales}
              allStoreNumber={allStoreNumber}
              setAllStoreNumber={setAllStoreNumber}
              storeNumber={storeNumber}
              setStoreNumber={setStoreNumber}
              docomoDefinedIndustry={docomoDefinedIndustry}
              setDocomoDefinedIndustry={setDocomoDefinedIndustry}
              appliedIndustry={appliedIndustry}
              setAppliedIndustry={setAppliedIndustry}
              handlingProducts={handlingProducts}
              setHandlingProducts={setHandlingProducts}
              cost={cost}
              setCost={setCost}
              costAdjustInfo={costAdjustInfo}
              setCostAdjustInfo={setCostAdjustInfo}
              rateCriteria={rateCriteria}
              setRateCriteria={setRateCriteria}
              remark={remark}
              setRemark={setRemark}
              //エラーメッセージ
              inclusionMerchantErrorMsg={comprehensiveMerchantErrorMsg}
              setInclusionMerchantErrorMsg={setComprehensiveMerchantErrorMsg}
              gwCompanyErrorMsg={gwCompanyErrorMsg}
              setGwCompanyErrorMsg={setGwCompanyErrorMsg}
              answerDateErrorMsg={answerDateErrorMsg}
              setAnswerDateErrorMsg={setAnswerDateErrorMsg}
              applicationDateErrorMsg={applicationDateErrorMsg}
              setApplicationDateErrorMsg={setApplicationDateErrorMsg}
              answerRateErrorMsg={answerRateErrorMsg}
              setAnswerRateErrorMsg={setAnswerRateErrorMsg}
              desiredRateErrorMsg={desiredRateErrorMsg}
              setDesiredRateErrorMsg={setDesiredRateErrorMsg}
              salesErrorMsg={salesErrorMsg}
              setSalesErrorMsg={setSalesErrorMsg}
              allStoreNumberErrorMsg={allStoreNumberErrorMsg}
              setAllStoreNumberErrorMsg={setAllStoreNumberErrorMsg}
              storeNumberErrorMsg={storeNumberErrorMsg}
              setStoreNumberErrorMsg={setStoreNumberErrorMsg}
              appliedIndustryErrorMsg={appliedIndustryErrorMsg}
              docomoDefinedIndustryErrorMsg={docomoDefinedIndustryErrorMsg}
              setDocomoDefinedIndustryErrorMsg={
                setDocomoDefinedIndustryErrorMsg
              }
              setAppliedIndustryErrorMsg={setAppliedIndustryErrorMsg}
              handlingProductsErrorMsg={handlingProductsErrorMsg}
              setHandlingProductsErrorMsg={setHandlingProductsErrorMsg}
              costErrorMsg={costErrorMsg}
              setCostErrorMsg={setCostErrorMsg}
              costAdjustInfoErrorMsg={costAdjustInfoErrorMsg}
              setCostAdjustInfoErrorMsg={setCostAdjustInfoErrorMsg}
              rateCriteriaErrorMsg={rateCriteriaErrorMsg}
              setRateCriteriaErrorMsg={setRateCriteriaErrorMsg}
              remarkErrorMsg={remarkErrorMsg}
              setRemarkErrorMsg={setRemarkErrorMsg}
              // 入力チェック
              checkRequired={checkRequired}
              checkAnswerRate={checkAnswerRate}
              checkDesiredRate={checkDesiredRate}
              checkSales={checkSales}
              checkStoreNumber={checkStoreNumber}
              checkLengthHundred={checkLengthHundred}
              checkCostAdjustInfo={checkCostAdjustInfo}
              checkRemark={checkRemark}
            />
            <ColumnLayout columns={2}>
              <Box float="left">
                <Button
                  formAction="none"
                  variant="link"
                  onClick={onClickReturn}
                >
                  {COMMON_LABELS.BTNS.BACK}
                </Button>
              </Box>
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xxl">
                  {!isRegister ? (
                    <Button onClick={onClickDelete}>
                      {COMMON_LABELS.BTNS.DELETE}
                    </Button>
                  ) : (
                    ""
                  )}
                  <Button
                    variant="primary"
                    onClick={isRegister ? onClickRegister : onClickUpdate}
                  >
                    {isRegister
                      ? COMMON_LABELS.BTNS.REGISTER
                      : COMMON_LABELS.BTNS.UPDATE}
                  </Button>
                </SpaceBetween>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
        </SpaceBetween>
      </Form>
    </form>
  );
};
export default Main;
